import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Clock, ShieldCheck, Users } from "lucide-react";

import { FaqSection } from "@/components/FaqSection";
import { LeadForm } from "@/components/LeadForm";
import { PageHeader } from "@/components/PageHeader";
import { listFaqs } from "@/lib/content.functions";
import { breadcrumbSchema, canonical, pageMeta } from "@/lib/seo";
import { site, whatsappUrl } from "@/lib/site";

const faqsQuery = queryOptions({
  queryKey: ["faqs", "/analise"],
  queryFn: () => listFaqs({ data: { path: "/analise" } }),
});

export const Route = createFileRoute("/analise")({
  loader: ({ context }) => context.queryClient.ensureQueryData(faqsQuery),
  head: () => ({
    meta: pageMeta({
      title: "Solicite uma análise da sua empresa | Patrimonial Soluções em Negócios",
      description:
        "Conte como sua operação funciona hoje e receba uma análise da equipe da Patrimonial sobre a contabilidade da sua empresa. Sem compromisso.",
      path: "/analise",
    }),
    links: canonical("/analise"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema([{ name: "Análise", path: "/analise" }])),
      },
    ],
  }),
  component: Page,
  errorComponent: () => (
    <div className="container-page py-20">
      <p className="text-base text-muted-foreground">
        Não foi possível carregar esta página agora. Atualize em instantes.
      </p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-page py-20">
      <p className="text-base text-muted-foreground">Conteúdo não encontrado.</p>
    </div>
  ),
});

const highlights = [
  {
    icon: Clock,
    title: "Leva cerca de 2 minutos",
    text: "Três etapas curtas sobre o perfil da empresa, o cenário contábil e a melhor forma de contato.",
  },
  {
    icon: Users,
    title: "Quem analisa é a equipe",
    text: `A análise é feita por profissionais da Patrimonial, sob a direção de ${site.director}.`,
  },
  {
    icon: ShieldCheck,
    title: "Sem compromisso",
    text: "Você recebe um retorno com o caminho recomendado. Seguir ou não é decisão sua.",
  },
];

function Page() {
  const { data } = useSuspenseQuery(faqsQuery);

  return (
    <>
      <PageHeader
        title="Solicite uma análise da sua empresa"
        intro="Um formulário curto para entendermos a sua operação. A equipe analisa o cenário e retorna com um caminho claro, sem compromisso."
        crumbs={[{ name: "Análise", path: "/analise" }]}
      />

      <section className="py-14 md:py-20">
        <div className="container-page grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <LeadForm sourcePath="/analise" />

          <div className="space-y-6">
            {highlights.map((item) => (
              <div key={item.title} className="flex gap-4">
                <span className="mt-1 inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-brand">
                  <item.icon className="size-5" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-base font-semibold text-foreground">{item.title}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              </div>
            ))}

            <div className="rounded-lg border border-border bg-surface p-6">
              <h2 className="text-base font-semibold text-foreground">Prefere conversar agora?</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Fale com a equipe pelo WhatsApp ou ligue para {site.phone}.
              </p>
              <a
                href={whatsappUrl("/analise")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex min-h-11 items-center rounded-md border border-input bg-background px-5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
              >
                Falar pelo WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <FaqSection faqs={data.faqs} />
    </>
  );
}
